import { useHistory } from "react-router-dom";
import { groupOfPeopleJoyfulCoins } from "swing-assets";
import { Button, Card, Colors, Icon } from "swing-components";

import { translate } from "~utils";

/***** Types *****/
type NonSwingCertCardProps = {
  state?: "CA" | "NJ";
  onClick: () => void;
  hasSwingCert?: boolean;
  onboardingConfigurationId?: string;
  isSwingCertFeatureEnabled?: boolean;
};

/**
 * Component | NonSwingCertCard
 *
 * Renders a dismissible promotional card encouraging users to register for
 * SwingCert to access more job opportunities and higher pay rates.
 *
 * Conditionally routes to internal permit page or external registration
 * based on user state and configuration.
 */
export function NonSwingCertCard(props: NonSwingCertCardProps) {
  const { state, onClick, hasSwingCert, onboardingConfigurationId, isSwingCertFeatureEnabled } =
    props;

  /*** Hooks ***/
  const history = useHistory();

  /*** Render #1 ***/
  if (state === undefined) return null;

  /*** Handlers ***/
  function handleClick() {
    // Call the onClick prop (handles card dismissal or other logic)
    onClick();

    // If user is in CA, doesn't have SwingCert, has the ca-ukg-swing-cert config,
    // AND the feature flag is enabled, redirect to the new /more/permit page
    if (
      state === "CA" &&
      hasSwingCert === false &&
      onboardingConfigurationId === "ca-ukg-swing-cert" &&
      isSwingCertFeatureEnabled === true
    ) {
      history.push("/more/permit");
    } else {
      // Otherwise, open external registration link
      const url = `https://start.swingeducation.com/swingcert-${state!.toLowerCase()}-register`;
      window.open(url, "_blank", "noreferrer");
    }
  }

  /*** Render #2 ***/
  return (
    <div css={{ position: "relative" }}>
      <Card borderColor={Colors.slate200}>
        {/* Close Button */}
        <button
          css={{ position: "absolute", top: 9, right: 9, background: "none" }}
          onClick={onClick}
        >
          <Icon name="Close" color={Colors.blue500} />
        </button>
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            backgroundColor: Colors.green200,
            // Using negative margin to offset Card padding
            // so bg can span full width — design confirmed
            // this is a one-off
            margin: -16,
            padding: 16,
            borderRadius: 8,
          }}
        >
          <img src={groupOfPeopleJoyfulCoins} alt={translate("NON_SWING_CERT_CARD_IMG_ALT_TXT")} />

          <div css={{ textAlign: "center", fontSize: 14, lineHeight: "24px" }}>
            <span css={{ fontWeight: 600 }}>{translate("NON_SWING_CERT_CARD_HEADER")}</span>
            <p css={{ margin: 0 }}>{translate("NON_SWING_CERT_CARD_SUBHEADER", { state })}</p>
          </div>
          <Button onClick={handleClick}>
            {translate("NON_SWING_CERT_CTA", { multiplier: state === "CA" ? 2 : 3 })}
          </Button>
        </div>
      </Card>
    </div>
  );
}
